import { Organization, type OrganizationManager } from "@entities";
import type { UserProviderManager } from "../../contexts/UserProvider";
import LogoutIcon from "@mui/icons-material/Logout";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Typography,
  type SelectChangeEvent,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { organizationManagerRepo } from "@repositories";
import { linko } from "@shared/linko";
import { BaseRoles } from "@shared/Roles";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { remult } from "remult";
import { type PhoneNumber } from "server/src/entities/otp/types";
import { FormGoogleAutocomplete } from "../../components/form-components/form-google-autocomplete/FormGoogleAutocomplete";
import { FormTextField } from "../../components/form-components/form-text-field/FormTextField";
import { UploadImage } from "../../components/form-components/image-uploader/ImageUploader";
import { GenericDialog } from "../../components/generic-components/generic-dialog/GenericDialog";
import SmsReceiver from "../../components/sms-receiver/SmsReceiver";
import { genders } from "../../consts/profilePage.conts";
import { useAlert } from "../../contexts/AlertProvider";
import { useUser } from "../../contexts/UserProvider";
import { formatPhoneNumber } from "../../generic-functions/format-phone-number";
import FormProvider, { useForm } from "../../helpers/forms/FormProvider";
import { provide } from "../../helpers/wrap-and-provide";
import { getErrorMessage } from "../auth/Auth";
import "./ProfilePage.scss";

interface ProfileFormType {
  name: string;
  gender?: string;
  dateOfBirth?: Date;
  address?: string;
  email?: string;
  phoneNumber?: string;
}
interface ProfilePageProps {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  open: boolean;
}

const ProfilePage: React.FC<ProfilePageProps> = ({ setOpen, open }) => {
  const { setAlertText, setSeverity, setTime } = useAlert();
  const { setFieldValue, setValues, values } = useForm<ProfileFormType>();
  const [profileImg, setProfileImg] = useState<string>("");
  const { currentUser, updatedCurrentUser, logout } = useUser();
  const [tab, setTab] = useState<string>("details");
  const [organizationManagers, setOrganizationManagers] = useState<
    OrganizationManager[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOtp, setIsOtp] = useState<boolean>(false);
  const [selectedGender, setSelectedGender] = useState<{
    he: string;
    en: string;
  }>();
  const { login } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser) return;
    setProfileImg(currentUser.profileImageId || "");
    setInitialValues(currentUser);
  }, [currentUser?.id]);

  useEffect(() => {
    if (tab === "managers" && organizationManagers.length === 0) {
      getOrganizationsManagers();
    }
  }, [tab]);

  const setInitialValues = (userData: UserProviderManager) => {
    setValues({
      name: userData.name,
      // Address: userData.address, // Property removed from OrganizationManager
      // dateOfBirth: userData.dateOfBirth, // Property removed from OrganizationManager
      email: userData.email || "",
      phoneNumber: formatPhoneNumber(userData.phoneNumber || ""),
      gender: userData.gender ?? "else",
    });
  };

  async function saveData() {
    try {
      const { phoneNumber: _phoneNumber, ...restValues } = values;
      const updatedValues = {
        ...restValues,
        profileImageId: profileImg,
      };
      const updatedUser = await organizationManagerRepo.update(
        currentUser!.id,
        updatedValues as any,
      );
      if (updatedUser) {
        remult.user = {
          id: updatedUser.id,
          name: updatedUser.name,
          profileImageId: updatedUser.profileImageId,
          organizationId: updatedUser.organization?.id,
          phoneNumber: updatedUser.phoneNumber,
          roles: updatedUser.roles,
          memberId: updatedUser.id,
          tableSource: "organization-manager",
        };
        // Update specific fields - refetchUser will update currentUser automatically
        // No need to call updatedCurrentUser here since refetchUser is called at the end
        setSeverity("success");
        setAlertText("השמירה בוצעה בהצלחה");
        setTime(3000);
        setOpen(false);
      }
    } catch (error) {
      // eslint-disable-next-line no-console -- error handling
      setSeverity("warning");
      setAlertText("השמירה נכשלה אנא נסה ");
      setTime(3000);
    }
  }

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const selectedEn = e.target.value as string;
    const selected = genders.find((gender) => selectedEn === gender.en);
    if (selected) {
      setSelectedGender(selected);
    }
  };

  async function getOrganizationsManagers() {
    if (!currentUser?.phoneNumber) return;
    const managers = await Organization.getManagers(currentUser.phoneNumber);
    setOrganizationManagers(managers);
  }

  async function sendCode() {
    if (!currentUser) return;

    setIsLoading(true);

    try {
      await linko.otp.admin.getCode({
        phoneNumber: currentUser.phoneNumber as PhoneNumber,
      });
      setIsOtp(true);
    } catch (e) {
      setAlertText(getErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  }

  async function loginWithOtp(code: string) {
    setIsLoading(true);
    if (!currentUser) return;

    try {
      await linko.otp.admin.validateCode({
        code,
        phoneNumber: currentUser.phoneNumber as PhoneNumber,
      });
      await login();
      navigate("/volunteers", { replace: true });
      window.location.reload();
      setOpen(false);
    } catch (e) {
      setAlertText(getErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setTab(newValue);
    setIsOtp(false);
  };

  return (
    <GenericDialog
      onClose={() => setOpen(false)}
      open={open}
      setOpen={setOpen}
      maxWidth="md"
      fullWidth
    >
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          minHeight: "600px", // Set fixed minimum height
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h5" fontWeight="bold">
            פרופיל אישי
          </Typography>
          <Button
            startIcon={<LogoutIcon sx={{ transform: "rotate(180deg)" }} />}
            color="primary"
            onClick={logout}
            variant="outlined"
            size="small"
          >
            התנתקות
          </Button>
        </Box>

        <Tabs
          value={tab}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab value="details" label="פרטים אישיים" />
          <Tab value="managers" label="ארגונים" />
        </Tabs>

        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {tab === "details" ? (
            <Box display="flex" flexDirection="column" gap={3} sx={{ flex: 1 }}>
              <Box display="flex" alignItems="flex-start" gap={2}>
                <Box flex={1}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={8}>
                      <Box mb={2}>
                        <Typography variant="subtitle2" gutterBottom>
                          שם מלא
                        </Typography>
                        <FormTextField
                          name="name"
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box mb={2}>
                        <Typography variant="subtitle2" gutterBottom>
                          מגדר
                        </Typography>
                        <Select
                          labelId="gender-label"
                          value={selectedGender?.en || ""}
                          onChange={handleSelectChange}
                          fullWidth
                          sx={{
                            borderRadius: 2,
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderRadius: 2,
                            },
                          }}
                        >
                          {genders.map((gender, index) => (
                            <MenuItem key={index} value={gender.en}>
                              {gender.he}
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box mb={2}>
                        <Typography variant="subtitle2" gutterBottom>
                          תאריך לידה
                        </Typography>
                        <DatePicker
                          onChange={(value) =>
                            setFieldValue(
                              "dateOfBirth",
                              new Date(value as unknown as number),
                            )
                          }
                          value={dayjs(undefined)}
                          sx={{
                            width: "100%",
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box mb={2}>
                        <Typography variant="subtitle2" gutterBottom>
                          כתובת
                        </Typography>
                        <FormGoogleAutocomplete
                          name="address"
                          onLocationChange={(location) => {
                            setFieldValue("coordinates", location.latLng);
                            setFieldValue("address", location.locationName);
                          }}
                          placeValue=""
                          placeholder="רחוב, מספר, עיר"
                          fullWidth
                          InputProps={{
                            sx: {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box mb={2}>
                        <Typography variant="subtitle2" gutterBottom>
                          מספר טלפון
                        </Typography>
                        <FormTextField
                          name="phoneNumber"
                          fullWidth
                          disabled
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box mb={2}>
                        <Typography variant="subtitle2" gutterBottom>
                          אימייל
                        </Typography>
                        <FormTextField
                          name="email"
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  mt={12}
                >
                  <UploadImage
                    name="profileImageId"
                    imageId={profileImg}
                    handleImageChange={(imageId) => {
                      setProfileImg(imageId);
                      if (currentUser)
                        updatedCurrentUser("profileImageId", imageId);
                    }}
                  />
                </Box>
              </Box>

              <Box display="flex" justifyContent="center" mt={2}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={saveData}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    minWidth: 150,
                  }}
                >
                  שמירה
                </Button>
              </Box>
            </Box>
          ) : !isOtp ? (
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" mb={2}>
                בחר ארגון להתחברות:
              </Typography>
              <Grid container spacing={2}>
                {organizationManagers.length > 0 ? (
                  organizationManagers.map((manager, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Card
                        elevation={1}
                        sx={{
                          borderRadius: 2,
                          height: "100%", // Make all cards the same height
                          display: "flex",
                          flexDirection: "column",
                          transition: "transform 0.2s",
                          "&:hover": {
                            transform: "translateY(-3px)",
                            boxShadow: 3,
                          },
                        }}
                      >
                        <CardContent
                          sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <Typography variant="h6" gutterBottom>
                            {manager.organization?.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: "auto" }}
                          >
                            {manager.roles.includes(BaseRoles.Manager)
                              ? "מנהל מערכת"
                              : manager.roles.includes(BaseRoles.Admin)
                                ? "מנהל כללי"
                                : "מנהל"}
                          </Typography>
                          <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            onClick={sendCode}
                            sx={{ borderRadius: 2, mt: 2 }}
                            disabled={isLoading}
                          >
                            {isLoading ? "טוען..." : "התחבר"}
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Box
                      p={3}
                      textAlign="center"
                      sx={{
                        height: "200px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography color="text.secondary">
                        לא נמצאו ארגונים משויכים
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          ) : (
            <Box
              p={2}
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <SmsReceiver
                onResendCode={sendCode}
                smsLogin={loginWithOtp}
                isLoading={isLoading}
              />
            </Box>
          )}
        </Box>
      </Paper>
    </GenericDialog>
  );
};

export default provide([
  FormProvider,
  {
    initialValues: {
      name: "",
      gender: "",
      address: "",
      birthday: null,
      email: "",
      phoneNumber: "",
    },
    onSubmit: () => { },
  },
])(ProfilePage);
